import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  AppAlert,
  AppButton,
  AppCard,
  AppCheckbox,
  AppFormField,
  AppSelect,
  AppSpinner,
} from '../../../components/ui';
import SecteursZonePolygonEditor from '../../../components/secteurs/admin/SecteursZonePolygonEditor';
import secteursAdminService from '../../../services/secteursAdminService';
import secteursService from '../../../services/secteursService';
import { notify } from '../../../components/common/AppToast';
import { ROUTES } from '../../../config/routes';

const SecteursAdminZonePolygonPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [zone, setZone] = useState(null);
  const [secteurs, setSecteurs] = useState([]);
  const [polygonData, setPolygonData] = useState(null);
  const [centreData, setCentreData] = useState(null);
  const [secteurId, setSecteurId] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    Promise.all([
      secteursAdminService.getZone(id),
      secteursService.getSecteurs(),
    ])
      .then(([z, s]) => {
        setZone(z);
        setSecteurs(s);
        setPolygonData(z.polygon);
        setCentreData(z.centre);
        setSecteurId(z.secteur?.id ? String(z.secteur.id) : '');
        setActive(Boolean(z.active));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePolygonChange = (polygon, centre) => {
    setPolygonData(polygon);
    setCentreData(centre);
  };

  const handleSave = async () => {
    if (!polygonData) {
      notify.error('Dessinez ou modifiez un polygone avant de sauvegarder.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await secteursAdminService.updateZonePolygon(id, polygonData, centreData);
      await secteursAdminService.updateZone(id, {
        secteur_id: secteurId ? Number(secteurId) : null,
        active: active ? 1 : 0,
      });
      setMessage('Zone sauvegardée avec succès.');
      notify.success('Zone sauvegardée avec succès');
    } catch (err) {
      notify.error(err.message);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <AppSpinner />
      </MasterLayout>
    );
  }

  const couleur = zone?.secteur?.couleurSecteur || zone?.secteur?.couleur || '#007bff';

  return (
    <MasterLayout>
      <Breadcrumb
        title={`Éditeur — ${zone?.nomZone || 'Zone'}`}
        subtitle={`${zone?.nomZone} — ${zone?.codeCommune} (${zone?.codePostal})`}
        actions={(
          <div className="d-flex gap-2">
            <AppButton as={Link} to={ROUTES.secteurs.adminZones} size="sm" variant="outline-secondary">Retour</AppButton>
            <AppButton size="sm" variant="outline-primary" icon="mdi:form-textbox" onClick={() => navigate(ROUTES.secteurs.adminZoneEdit(id))}>
              Propriétés
            </AppButton>
          </div>
        )}
      />

      {error && <AppAlert variant="danger">{error}</AppAlert>}
      {message && <AppAlert variant="success">{message}</AppAlert>}

      <div className="row g-3">
        <div className="col-lg-8">
          <AppCard
            title="Carte interactive"
            subtitle="Utilisez les outils de dessin en haut à gauche de la carte"
            bodyClassName="p-0"
          >
            <SecteursZonePolygonEditor
              polygon={zone?.polygon}
              centre={zone?.centre}
              color={couleur}
              height={520}
              onChange={handlePolygonChange}
            />
          </AppCard>
        </div>
        <div className="col-lg-4">
          <AppCard>
            <h6 className="fw-semibold mb-3">Propriétés</h6>
            <AppAlert variant="info" className="text-sm py-2">
              <strong>Zone</strong> = commune · <strong>Secteur</strong> = groupe commercial
            </AppAlert>
            <AppFormField label="Secteur commercial" htmlFor="polygon-secteur">
              <AppSelect id="polygon-secteur" value={secteurId} onChange={(e) => setSecteurId(e.target.value)}>
                <option value="">Aucun secteur</option>
                {secteurs.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.nomCommercial}
                  </option>
                ))}
              </AppSelect>
            </AppFormField>
            <AppCheckbox
              id="zone-active"
              label="Zone active"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              wrapperClassName="mb-3"
            />
            <AppButton variant="success" className="w-100" onClick={handleSave} loading={saving} loadingLabel="Enregistrement…">
              Sauvegarder
            </AppButton>
          </AppCard>
        </div>
      </div>
    </MasterLayout>
  );
};

export default SecteursAdminZonePolygonPage;
